import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from '@ionic/angular';
// import { Events } from '@ionic/angular';  // deprecated in Ionic 5
import { Events } from 'src/app/services/events/events.service'; 
import { Geolocation } from '@ionic-native/geolocation/ngx';

import { GlobalService } from '../global/global.service';
import { CommonService } from '../common/common.service';
import { GeoStatus, GeoPosition } from './geoposition';
import { RuntimeService } from '../runtime/runtime.service';

// add global variables here
import * as Constants from '../constants/constants';

// third party libraries here
// https://forum.ionicframework.com/t/how-to-include-moment-js-in-ionic4/145819
import * as moment from 'moment'; 
import { Observable } from 'rxjs/internal/Observable';

// prevent any warnings from TypeScript about the google object that the Google Maps SDK makes available to us
declare let google; 
// declare let IndoorAtlas;
let IA_API_KEY = window["IA_API_KEY"];        // gain access to public variables
let IA_API_SECRET = window["IA_API_SECRET"];  // gain access to public variables

// noop implementation of IndoorAtlas due to build errors
class IndoorAtlas {
  static setPosition(x, y, z) {}
  static initialize(x) {
    return {
      watchPosition: (y) => {}
    }
  }
  static clearWatch() {}
  static watchPosition(x, y, z) {}
  static async getCurrentPosition() {}
}

/*
  PETER MATUCHNIAK 2018-03-07
  Determine the geolocation
  ref: https://ionicframework.com/docs/native/geolocation/
  ref: https://www.gajotres.net/ionic-2-having-fun-with-cordova-geolocation-plugin/
*/

/*
Location accuracy
Google Maps assigns additional location accuracy values to each location mapped and defines these values as such:

"ROOFTOP" indicates that the returned result is a precise geocode for which we have location information accurate down to street address precision.
"RANGE_INTERPOLATED" indicates that the returned result reflects an approximation (usually on a road) interpolated between two precise points (such as intersections). Interpolated results are generally returned when rooftop geocodes are unavailable for a street address.
"GEOMETRIC_CENTER" indicates that the returned result is the geometric center of a result such as a polyline (for example, a street) or polygon (region).
"APPROXIMATE" indicates that the returned result is approximate.
For addresses that Google Maps is not able to map (whether for a lack of location data or inaccurate location data), Maptive separates out this data for quick export back to a spreadsheet for correction.
*/


@Injectable({
  providedIn: 'root'
})
export class GeolocationWrapperService {
  private consoleLog:boolean = false;

  private gpsObserver: any;
  public gpsObservable: any;

  private ipsObserver: any;
  public ipsObservable: any;

  //private notifyPosition:GeoPosition;
  private lastStatus:GeoStatus = new GeoStatus();
  private ipsStatus:GeoStatus = new GeoStatus();
  private gpsStatus:GeoStatus = new GeoStatus();

  private lastPosition:GeoPosition;
  private ipsPosition:GeoPosition;
  private gpsPosition:GeoPosition;
  private bestPosition:GeoPosition;

  private _IpsPositionCount:number = 0;
  private _GpsPositionCount:number = 0;

  // not all used just yet (copied from original GeolocationService)

  //private _isReady:boolean   = true;
  private _isIpsInitialized:boolean = false; 
  //private _isGpsReady:boolean = false; 

  private _IpsWatchId:any = null;
  private _GpsWatchId:any = null;
  
  private _isIpsEnabled:boolean = false;
  private _isGpsEnabled:boolean = false;

  private activeWatches: Array<string> = []; // based on constants.WATCH_MONITOR etc.

  //private _isActiveMap:boolean = false;
  //private _isActiveMonitor:boolean = false;
  //private _isActiveSettings:boolean = false;
  private _isActiveWatchDependency:boolean = false;
  //private _isSetWatchingManually:boolean = false;
  
  // use the global field for this instead
  //private _checkinSiteIndoorFlag:boolean = true; // = false;

  private _IpsOptions = { timeout: 9000 }; // 9 seconds

  private _GpsOptions = 
    { enableHighAccuracy: true
    , timeout: 30000 // 60000
    , maximumAge: 0
    };

  //private _zoomLevel:number = 18;

  private _defaultFreshnessThresholdMinutes:number = 1;  
  private _defaultAccuracyThresholdMeters:number = 30; 

  private _isIpsException:boolean = false;
  private _IpsInitStatusMessage:string = "IPS not initialized";

  // using NgZone to force updates through to the UI:
  // // ref: https://www.thepolyglotdeveloper.com/2017/03/device-geolocation-nativescript-angular-application/
  
  constructor(
    public http: HttpClient
    , public platform: Platform
    , private runtimeProvider:RuntimeService
    , private common: CommonService
    , private globalProvider: GlobalService
    , private geolocationPlugin: Geolocation    // this is the actual plugin, that we are wrapping
    , public zone: NgZone 
    , public events: Events
    , public translate: TranslateService
  ) 
  {
    // console.log("IndoorAtlas.ctor IPS - IA_API_KEY key:" + IA_API_KEY);
    // console.log("IndoorAtlas.ctor IPS - IA_API_SECRET secret:" + IA_API_SECRET);
    // alert("IA_API_KEY:" + IA_API_KEY);

    // NOTE: the constructor ONLY gets called ONCE during the app life cycle
    this.consoleLog = this.globalProvider.consoleLog_geolocation;
    if (this.consoleLog) console.log('GeolocationWrapper.ctor - LifeCycle');
    //this.InitializeProvider();
    this.platform.ready().then(() => {
      if (this.consoleLog) console.log("GeolocationWrapper.ctor - platform ready!");
      this.InitializeProvider();
    });
  }

  InitializeProvider()
  {
    // this is only called ONCE during the app life-cycle
    if (this.consoleLog) console.log("GeolocationWrapper.InitializeProvider");
    this.SetGpsEnabledState();
    this.SetIpsEnabledState();
    //this.GetFirstLocation();
    this.InitialEmptyGpsPosition();
    this.InitialEmptyIpsPosition();

    this.SetupGpsObservable();
    this.SetupIpsObservable();
  }

  InitializeWatchDefault(isWatchRequired:boolean)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.InitializeWatchDefault:" + isWatchRequired);
    // do in the context of Settings so it can be toggled on and off there
    this.setWatchDependency(Constants.WATCH_INITIALIZE, isWatchRequired, "Geolocation.InitializeWatchDefault"); 
  }

  SetGpsEnabledState():boolean
  {
    // ToDo:  see if we can check the native location settings and determine this accordingly
    this._isGpsEnabled = true;

    return true;
  }
  SetIpsEnabledState():boolean
  {
    this._isIpsEnabled = false;
    if (!this.IsIpsSupportedOnPlatform()) { return false; }   // IPS requires NATIVE APP
    if (!this.IsOrganizationIpsEnabled()) { return false; }   // must be enabled in Organization db
    this._isIpsEnabled = true;

    return true;
  }

  InitialEmptyGpsPosition()
  {
    if (this.consoleLog) console.log("GeolocationWrapper.InitialEmptyGpsPosition");
    this.SetGpsStatus(false, false, "SETUP", "10", "Initializing", false);
    this.gpsPosition = this.CreateEmptyPosition(Constants.GPS, this.gpsStatus);
    this.lastPosition = this.common.DeepCopy(this.gpsPosition);
    this.bestPosition = this.common.DeepCopy(this.gpsPosition);
  }

  InitialEmptyIpsPosition()
  {
    if (this.consoleLog) console.log("GeolocationWrapper.InitialEmptyIpsPosition");
    this.SetIpsStatus(false, false, "SETUP", "10", "Initializing", false);
    this.ipsPosition = this.CreateEmptyPosition(Constants.INDOOR, this.ipsStatus);
  }

  SetupGpsObservable()
  {
    if (this.consoleLog) console.log("GeolocationWrapper.SetupGpsObservable");
    this.gpsObserver = null;
    this.gpsObservable = Observable.create(observer => {
        // NOTE: this gets called EVERY TIME a subscriber is registered from some other page
        this.gpsObserver = observer;
        if (this.consoleLog) console.log("GeolocationWrapper.SetupGpsObservable: CREATED");
    });
  }

  SetupIpsObservable()
  {
    if (this.consoleLog) console.log("GeolocationWrapper.SetupIpsObservable");
    this.ipsObserver = null;
    this.ipsObservable = Observable.create(observer => {
        // NOTE: this gets called EVERY TIME a subscriber is registered from some other page
        this.ipsObserver = observer;
        if (this.consoleLog) console.log("GeolocationWrapper.SetupIpsObservable: CREATED");
    });
  }

  // Call this from the app when ionic/cordova is ready
  // i.e. app.component.ts
  callInitializers(context:string)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.callInitializers - PageName:" + context);
    this.InitializeIps(Constants.WATCH_INITIALIZE , false);  // don't set autowatch on basic init
    this.InitializeGps();
  }

  /*
  // Note: I liked this way of setting up a subscription to location changes
  //       but there are a couple of issues doing it this way
  //       (1) it is different from the IPS approach, and my preference is to be consistent
  //       (2) this starts the watch and subscription in one go, rather than being
  //           able to control either one separately from an external perspective
  //           (e.g. we may not want to always be listening, but still subscribe to changes)
  SetGpsListener(isStartSubscribe:boolean)
  {
    if (this.consoleLog) console.log('GeolocationWrapper.SetGpsListener ' + isStartSubscribe);
    try
    {
      if (isStartSubscribe)
      {
        this.gpsSubscription = this.geolocation.watchPosition()
          //.filter((p) => p.coords !== undefined) //Filter Out Errors
          .subscribe(position => 
            {
              //if (this.consoleLog) console.log("GeolocationWrapper.watchPosition RESPONSE", position); // may or may not be okay
              let notifyPosition = this.onGpsResponse(position, Constants.GEO_WATCH);
              this.gpsObserver.next(notifyPosition); 
            }
            , error => 
            {
              //if (this.consoleLog) console.log("GeolocationWrapper.watchPosition ERROR", error);
              let notifyPosition = this.onGpsError(error, Constants.GEO_WATCH);
              this.gpsObserver.next(notifyPosition); 
            }
          );
          if (this.isGpsWatching())
          {
            if (this.consoleLog) console.log("GeolocationWrapper.SetGpsListener GPS ALREADY WATCHING");
            // could clear and start again?
            return;
          }
      }
      else
      {
        if (this.gpsSubscription !== undefined) { this.gpsSubscription.unsubscribe(); }
      }
    }
    catch (error)
    {
      console.error("GeolocationWrapper.SetGpsListener(" + isStartSubscribe + ") - ERROR", error);
    }
  }
  */

  GpsWatch(invokeGpsListener:boolean)
  {
    if (this.consoleLog) console.log('GeolocationWrapper.GpsWatch  invoke:' + invokeGpsListener);

    try
    {
      if (invokeGpsListener)
      {
          if (this.isGpsWatching())
          {
            if (this.consoleLog) console.log("GeolocationWrapper.GpsWatch - GPS ALREADY WATCHING");
            if (this.gpsObserver) this.gpsObserver.next(this.gpsPosition); 
            // could clear and start again?
            return;
          }

          let isHintProvidedToIps:boolean = false;
          this.gpsStatus.ActiveWatch = this.isGpsWatching();
          if (this.gpsObserver) this.gpsObserver.next(this.gpsPosition); // send what we have so far
          this.SetGpsStatus(true, false, "WATCH", "GPS60", "Starting location watch", false);

          console.warn('GeolocationWrapper.GpsWatch - about to start watching GPS');
          this._GpsWatchId = navigator.geolocation.watchPosition(position => 
            {
              this.gpsStatus.ActiveWatch = this.isGpsWatching();
              this.zone.run(() => {
                if (this.consoleLog) console.log("GeolocationWrapper.GpsWatch - GPS RESPONSE", position); // may or may not be okay
                let notifyPosition = this.onGpsResponse(position, Constants.GEO_WATCH, Constants.GEO_SUCCESS_WATCH);
                if (this.gpsObserver) this.gpsObserver.next(notifyPosition);

                if (!isHintProvidedToIps) 
                { 
                  this.ProvideHintToIps(notifyPosition);
                  isHintProvidedToIps = true;
                }
              });
            }
            , error => 
            {
              this.gpsStatus.ActiveWatch = this.isGpsWatching();
              this.zone.run(() => {
                if (this.consoleLog) console.log("GeolocationWrapper.GpsWatch - GPS ERROR", error);
                this.onGpsError(error, Constants.GEO_WATCH);
              });
            }
            ,
            this._GpsOptions
            //, 
            //{timeout: TIMEOUT}
          );
      }
      else
      {
        if ((this.isGpsWatching()) && (this._isActiveWatchDependency) )
        {
          if (this.consoleLog) console.log("GeolocationWrapper.GpsWatch: ignoring STOP request as WATCH DEPENDENCY is active");
          if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition); 
          return;
        }

        if (this.consoleLog) console.log('GeolocationWrapper.GpsWatch: stop watching GPS');
        navigator.geolocation.clearWatch(this._GpsWatchId);
        this._GpsWatchId = null; 
        this._GpsPositionCount = 0;
        this.SetGpsStatus(true, false, "WATCH", "GPS50", "Watch stopped", false);
      }
    }
    catch (error)
    {
      console.error("GeolocationWrapper.GpsWatch(" + invokeGpsListener + ") - ERROR", error);
      this.SetGpsStatus(false, true, "WATCH", "GPS90", "Unexpected Watch error", false);
    }
  }

  IpsWatch(invokeIpsListener:boolean)
  {
    if (this.consoleLog) console.log('GeolocationWrapper.IpsWatch invoke:' + invokeIpsListener);

    try
    {
      if (!this.IsIpsSupportedOnPlatform()) { return; }   // IPS requires NATIVE APP
      if (!this.IsOrganizationIpsEnabled()) { return; }   // must be enabled in Organization db
      
      if (invokeIpsListener)
      {
        if (!this.isLocationIndoors())              // must be in a known INDOOR location
        { 
          console.warn('GeolocationWrapper.IpsWatch: must be in a mapped indoor location');
          this.SetIpsStatus(false, true, "WATCH", "IPS17", "Not in a mapped indoor location", false);
          return;
        }  

        if (!this._isIpsInitialized) 
        { 
          console.warn('GeolocationWrapper.IpsWatch: IPS not initialized (not ready)');
          this.SetIpsStatus(false, false, "WATCH", "IPS18", "Indoor not initialized", false);
          //this.InitializeIps();
          return; 
        }

        if (this.isIpsWatching())
        {
          if (this.consoleLog) console.log("GeolocationWrapper.IpsWatch - IPS ALREADY WATCHING");
          if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition); 
          // could clear and start again?
          return;
        }

        if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition); // send what we have so far
        this.SetIpsStatus(true, false, "WATCH", "IPS60", "Starting location watch", false);

        console.warn('GeolocationWrapper.IpsWatch - about to start watching IPS');
        this.ipsStatus.ActiveWatch = this.isIpsWatching();
        this._IpsWatchId = IndoorAtlas.watchPosition(position => 
          {
            this.ipsStatus.ActiveWatch = this.isIpsWatching();
            this.zone.run(() => {
              if (this.consoleLog) console.log("GeolocationWrapper.IpsWatch IPS RESPONSE", position); // may or may not be okay
              let notifyPosition = this.onIpsResponse(position, Constants.GEO_WATCH, Constants.GEO_SUCCESS_WATCH);
              if (this.ipsObserver) this.ipsObserver.next(notifyPosition); 
            });
          }
          , error => 
          {
            this.ipsStatus.ActiveWatch = this.isIpsWatching();
            this.zone.run(() => {
              if (this.consoleLog) console.log("GeolocationWrapper.IpsWatch IPS ERROR", error);
              this.onIpsError(error, Constants.GEO_WATCH);
            });
          }
          , 
          this._IpsOptions
        );
      }
      else
      {
        let checkinSiteIndoorFlag = this.globalProvider.AmbitConfig.CheckinSiteIndoorFlag;
        if ((this.isIpsWatching) && (this._isActiveWatchDependency) && (checkinSiteIndoorFlag))
        {
          if (this.consoleLog) console.log("GeolocationWrapper.IpsWatch: ignoring STOP request as WATCH DEPENDENCY is active (and CheckinSite is indoors)");
          if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition); 
          return;
        }

        if (this.consoleLog) console.log('GeolocationWrapper.IpsWatch: stop watching IPS');
        IndoorAtlas.clearWatch(); // IndoorAtlas.clearWatch(this._IpsWatchId);
        this._IpsWatchId = null;
        this._IpsPositionCount = 0;
        this.SetIpsStatus(true, false, "WATCH", "IPS50", "Watch stopped", false);
      }
    }
    catch (error)
    {
      console.error("GeolocationWrapper.IpsWatch(" + invokeIpsListener + ") - ERROR", error);
      this.SetIpsStatus(false, true, "WATCH", "IPS90", "Unexpected watch error", false);
    }
  }

  // // ------------------------------------------------------------

  SetGpsStatus(success:boolean, error:boolean, watchType:string, resultCode:string, resultText:string, suppressError:boolean):GeoStatus
  {
    // only show errors in the console log, unless the debug flag is on
    if (success)
    { 
      if (this.consoleLog) console.log("SetGpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }
    else if (error)
    {
      console.error("SetGpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }
    else // warning or FYI
    {
      if (this.consoleLog) console.warn("SetGpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }

    if (success) { this._GpsPositionCount++; }
    if (this.consoleLog) console.log("SetGpsStatus - _GpsPositionCount:" + this._GpsPositionCount);
    
    this.gpsStatus.IsEnabled = this._isGpsEnabled;
    this.gpsStatus.Success = success;
    this.gpsStatus.SuppressError = suppressError; // new 2019-06-13
    this.gpsStatus.WatchType = watchType;
    this.gpsStatus.ResultCode = resultCode;
    this.gpsStatus.ResultText = resultText;

    this.gpsStatus.PositionType = Constants.GPS;
    this.gpsStatus.PositionCount = this._GpsPositionCount;
    this.gpsStatus.ActiveWatch = this.isGpsWatching();

    this.gpsStatus.ResultTimestampEpoch = moment().unix() * 1000;
    this.gpsStatus.ResultTimestamp = moment().format("YYYY-MM-DDTHH:mm:ss"); // "DT-L";
    this.gpsStatus.ResultTimestampUtc = moment().utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"; // "DT-Z";  
         
    this.lastStatus = this.common.DeepCopy(this.gpsStatus);
    this.events.publish('geolocation:gpsStatus', this.gpsStatus);

    return this.gpsStatus;
  }

  SetIpsStatus(success:boolean, error:boolean, watchType:string, resultCode:string, resultText:string, suppressError:boolean):GeoStatus
  {
    // only show errors in the console log, unless the debug flag is on
    if (success)
    { 
      if (this.consoleLog) console.log("SetIpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }
    else if (error)
    {
      console.error("SetIpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }
    else // warning or FYI
    {
      if (this.consoleLog) console.warn("SetIpsStatus: " + success + " " + watchType + " - " + resultCode + ": " + resultText);
    }

    if (success) { this._IpsPositionCount++; }

    this.ipsStatus.IsEnabled = this._isIpsEnabled;
    this.ipsStatus.Success = success;
    this.ipsStatus.SuppressError = suppressError; // new 2019-06-13
    this.ipsStatus.WatchType = watchType;
    this.ipsStatus.ResultCode = resultCode;
    this.ipsStatus.ResultText = resultText;

    this.ipsStatus.PositionType = Constants.INDOOR;
    this.ipsStatus.PositionCount = this._IpsPositionCount;
    this.ipsStatus.ActiveWatch = this.isIpsWatching();

    this.ipsStatus.ResultTimestampEpoch = moment().unix() * 1000;
    this.ipsStatus.ResultTimestamp = moment().format("YYYY-MM-DDTHH:mm:ss"); // "DT-L";
    this.ipsStatus.ResultTimestampUtc = moment().utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"; // "DT-Z";  
         
    this.lastStatus = this.common.DeepCopy(this.ipsStatus);
    this.events.publish('geolocation:ipsStatus', this.ipsStatus);

    return this.ipsStatus;
  }

  // // ------------------------------------------------------------

  onGpsResponse(position:any, watchType:string, resultText:string): GeoPosition
  {
    //if (this.consoleLog) console.log("GeolocationWrapper.onGpsResponse(" + context + ")", position);
    if ((!position) || (position.coords === undefined))
    {
      // e.g. when there is no network connection
      // {code: 2, message: "Network location provider at 'https://www.googleapis.com/' : No response received."}
      this.onGpsError(position, watchType) ; // no position information found
      return this.gpsPosition;
    }

    let geoStatus:GeoStatus = this.SetGpsStatus(true, false, watchType, "OK", resultText, false);
    
    this.gpsPosition = this.SetPosition(position, Constants.GPS, watchType, geoStatus);
    this.lastPosition = this.common.DeepCopy(this.gpsPosition);
    this.bestPosition = this._saveBestLocationForApiCalls(this.lastPosition, Constants.GPS); 
    this.events.publish('geolocation:gpsPosition', this.gpsPosition); 
    if (this.consoleLog) console.log("GeolocationWrapper.onGpsResponse(" + watchType + ") OK - bestPosition:", this.bestPosition);
    
    return this.gpsPosition; //bestPosition;
  }

  onGpsError(error, watchType:string)
  {
    console.error("GeolocationWrapper.onGpsError(" + watchType + ")", error);
    let resultCode = (error && error.code)? error.code : "GPS99";
    let resultText = this.GetPositionErrorMessage(error, Constants.GPS);
    let suppressError = this.GetPositionSuppressError(error, Constants.GPS);
    this.SetGpsStatus(false, true, watchType, resultCode, resultText, suppressError);
  }

  // // ------------------------------------------------------------

  onIpsResponse(position:any, watchType:string, resultText:string): GeoPosition
  {
    if (this.consoleLog) console.log("GeolocationWrapper.onIpsResponse(" + watchType + ")", position);
    if ((!position) || (position.coords === undefined))
    {
      this.onIpsError(position, watchType) ; // no position information found
      return this.ipsPosition;
    }
    
    let geoStatus:GeoStatus = this.SetIpsStatus(true, false, watchType, "OK", resultText, false);
    this.ipsPosition = this.SetPosition(position, Constants.INDOOR, watchType, geoStatus);
    this.lastPosition = this.common.DeepCopy(this.ipsPosition);
    this.bestPosition = this._saveBestLocationForApiCalls(this.lastPosition, Constants.INDOOR); 
    this.events.publish('geolocation:ipsPosition', this.ipsPosition); 
 
    if (this.consoleLog) console.log("GeolocationWrapper.onIpsResponse(" + watchType + ") OK - ipsPosition:", this.ipsPosition);
    return this.ipsPosition;
  }

  onIpsError(error, watchType:string)
  {
    console.error("GeolocationWrapper.onIpsError(" + watchType + ")", error);
    let resultCode = (error && error.code)? error.code : "IPS99";
    let resultText = this.GetPositionErrorMessage(error, Constants.INDOOR);
    let suppressError = this.GetPositionSuppressError(error, Constants.INDOOR);
    this.SetIpsStatus(false, true, watchType, resultCode, resultText, suppressError);
  }
  // // ------------------------------------------------------------

  GetPositionErrorMessage(error:any, positionType:string): string
  {
    if (this.consoleLog) console.log("GeolocationService._GetGpsPositionError - Error: ", error);
    let MainErrorMessage = "Cannot determine location (0)" ; // start with a default

    if (positionType = Constants.GPS)
    {
      if (error.message)        { MainErrorMessage = error.message; }
      else if (!error.code)     { MainErrorMessage = 'Cannot determine location'; }
      else if (error.code === 1) { MainErrorMessage = 'Location permission denied'; }
      else if (error.code === 2) { MainErrorMessage = 'Location position unavailable'; }
      else if (error.code === 3) { MainErrorMessage = 'Location timeout'; }
      else { MainErrorMessage = 'Cannot determine location'; }
    }
    else
    {
      if (error.message)        { MainErrorMessage = error.message; }
      else if (!error.code)     { MainErrorMessage = 'Cannot determine Indoor location'; }
      else if (error.code === 1) { MainErrorMessage = 'Indoor Location permission denied'; }
      else if (error.code === 2) { MainErrorMessage = 'Indoor Location position unavailable'; }
      else if (error.code === 3) { MainErrorMessage = 'Indoor Location timeout'; }
      else if (error.code === 4) { MainErrorMessage = 'Indoor Invalid Access Token'; }
      else if (error.code === 5) { MainErrorMessage = 'Indoor Initialization Error'; }
      else if (error.code === 6) { MainErrorMessage = 'Indoor Floor plan unavailable'; }
      else { MainErrorMessage = 'Cannot determine Indoor location'; }
    }

    return this.myTranslate(MainErrorMessage); // + " (" + positionType + ")";
  }

  GetPositionSuppressError(error:any, positionType:string): boolean
  {
    if (this.consoleLog) console.log("GeolocationService.GetPositionSuppressError - Error: ", error);

    if (!error) { return false; }

    // ref:  https://stackoverflow.com/questions/30989428/apache-cordova-geolocation-watchposition-times-out-on-ios-when-standing-still/31074901#31074901
    // ref: https://github.com/dburles/meteor-google-maps/issues/64

    // suppress timeout messages for now
    if (error.code === 3)
    {
      return true;
    }

    if (error.message)
    {
      let message:string = error.message;
      if (message.indexOf('timed out') >= 0)
      {
        return true;
      }
    }
    
    return false;
  }
  
  myTranslate(value:string):string
  {
    // WARNING! inline translation will CRASH if the value is empty
    if (!value) { return ""; }
    return this.translate.instant(value);
  }


  InitializeGps()
  {
    this.SetGpsStatus(false, false, "SETUP", "GPS10", "Initializing", false);

    if (!navigator.geolocation) // (!$window.navigator.geolocation) 
    {
      this.SetGpsStatus(false, true, "SETUP", "GPS29", "Location access denied", false);
    }
    else
    {
      this.SetGpsStatus(false, false, "SETUP", "GPS20", "Acquiring Position", false);
      this.GetGpsLocationOneTime(true);
    }
  }

  // Send information to all the subscribers when this is called
  public TriggerGpsInformation() {
    if (this.consoleLog) console.log("Notification.TriggerGpsInformation:" + (this.gpsObserver !== undefined));
    if (this.gpsObserver) this.gpsObserver.next(this.gpsPosition); 
  }
  public TriggerIpsInformation() {
    if (this.consoleLog) console.log("Notification.TriggerIpsInformation:" + (this.ipsObserver !== undefined));
    if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition); 
  }

  // // ------------------------------------------------------------
  //          INDOOR ATLAS POSITIONING STUFF
  // // ------------------------------------------------------------

  public IsOrganizationIpsEnabled():boolean
  {
    // isOrganizationIpsEnabled
    return this.globalProvider.AmbitConfig.EnableIndoorFlag;
  }

  public IsCheckinSiteIpsEnabled():boolean
  {
    // isOrganizationIpsEnabled
    return this.globalProvider.AmbitConfig.CheckinSiteIndoorFlag;
  }

  public IsIpsSupportedOnPlatform()
  {
    //return !this.runtimeProvider._isBrowser;
    return this.runtimeProvider.IsCordova();
  }

  IsIpsAvailableForOrgAndPlatform():boolean
  {
    if (!this.IsOrganizationIpsEnabled())
    {
      console.warn('GeolocationWrapper.IsIpsAvailable: Indoor not enabled for this organization');
      this.SetIpsStatus(false, false, "SETUP", "IPS39", "Indoor not enabled for organization", false);
      return false;
    }

    if (!this.IsIpsSupportedOnPlatform())
    {
      if (this.consoleLog) console.warn('GeolocationWrapper.IsIpsAvailable: Indoor only available in mobile app');
      this.SetIpsStatus(false, false, "SETUP", "IPS49", "Indoor only available in mobile app", false);
      return false;
    }

    return true;
  }

  public InitializeIps(watchActivity:string, autoWatch:boolean)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.InitializeIps: calling IndoorAtlas.initialize " + watchActivity + ":" + autoWatch);

    if (!this.IsIpsAvailableForOrgAndPlatform())
    {
      if (this.consoleLog) console.log("GeolocationWrapper.InitializeIps: cannot proceed due to not available for org/platform");
      return;
    }
    
    // NOTE: we still want to initialize IPS even if the site we are currently in doesn't support it 
    // BUT for now let's avoid the initialization until they checkin to a site that is known for it
    
    if (!this.isLocationIndoors())
    {
      if (this.consoleLog) console.log("GeolocationWrapper.InitializeIps: will not proceed due to checked in site is not indoors");
      return;
    }

    // THIS IS GETTING KLUDGY SINCE THE UPDATE TO IndoorAtlas CHANGED ITS BEHAVIOUR
    // ToDo: go through the new documentation and cleanup / improve the capabilities of IPS
    if (this._isIpsInitialized)
    {
      if (autoWatch)
      {
        if (this.consoleLog) console.log("GeolocationWrapper.InitializeIps: is already initialized -- returning here AFTER setting watch");
        this.setWatchDependency(watchActivity, true, "Geolocation.InitializeIps");
      }
      else
      {
        if (this.consoleLog) console.log("GeolocationWrapper.InitializeIps: is already initialized -- returning here");
      }
      return;
    }
    else
    {
      console.log("GeolocationWrapper.InitialializeIps: not initialized yet");
    }

    console.warn('GeolocationWrapper.InitializeIps: Calling Indoor Atlas');
    this.SetIpsStatus(false, false, "SETUP", "IPS20", "Initializing Indoor feature", false);
    
    //if (this.consoleLog) // Get IndoorAtlas API Key and Secret from here https://app.indooratlas.com/apps
    //var IA_API_KEY = '58064846-eae3-4007-a211-49e501c41fd2'; // p.mx@g  49e501c41fd2
    //var IA_API_SECRET = 't73/Pn0osz7R09j6JMs8lzVEMQTc/9C7Wm41pOQVeU1CXc4OzkkRh5E03fTIulfjcL1Vxt1C1c9axmJRVOCaHAAUztVObSsp0XcXitEzT74qCHC28Duwk2FSohN8tQ==';
    if (this.consoleLog) console.log("IndoorAtlas.initialize.try IPS - key:" + IA_API_KEY);
    // if (this.consoleLog) console.log("IndoorAtlas.initialize.try IPS - secret:" + IA_API_SECRET);
    try
    {
      IndoorAtlas.initialize({ apiKey: IA_API_KEY })
        .watchPosition(this.onIpsWatch.bind(this, watchActivity, autoWatch))
        //.watchFloorPlan(floorPlanSelector.onFloorPlanChange.bind(floorPlanSelector)) // ToDo: add support for this
        //.watchVenue(floorPlanSelector.onVenueChange.bind(floorPlanSelector))         // ToDo: add support for this
        //.onStatusChanged(console.log)   // uncomment to debug os show more info
        //.getTraceId(console.log)        // uncomment to debug os show more info
        ;
      /*
      NEW:

      // initialize and start positionins
      IndoorAtlas.initialize({ apiKey: IA_API_KEY })
        .watchPosition(function (position) {
          console.log(
            "latitude: " + position.coords.latitude + ", " +
            "longitude: " + position.coords.longitude + ", " +
            "floor: " + position.coords.floor);
        })
        .watchVenue(function (venue) {
          if (venue) {
            console.log(
              "entered venue " + venue.name + " with " +
              venue.floorPlans.length + " floor plan(s)");
          }
        })
        .watchFloorPlan(function (floorPlan) {
          if (floorPlan) {
            console.log("entered floor plan " + floorPlan.name + ", id: " + floorPlan.id);
          } else {
            console.log("moved outdoors");
          }
        });


      OLD:
      IndoorAtlas.initialize
      ( this._onIndoorAtlasInitializeSuccess
      , this._onIndoorAtlasInitializeError
      , { key: Constants.IA_API_KEY
        , secret: Constants.IA_API_SECRET
        }
      );
      */
    }
    catch (error)
    {
      console.error('IndoorAtlas.initialize.catch: Exception ', error);
      this.SetIpsStatus(false, true, "WATCH", "IPS99", "Exception initializing Indoor", false);
      this._isIpsException = true;
      if ((error) && (error.message))
      {
        this._IpsInitStatusMessage = "IPS99: " + error.message;
      }
      else
      {
        this._IpsInitStatusMessage = "IPS99: Initialize error - Check license key";
      }
      // alert("Unable to initialize Indoor IPS - check license key");
    }
  }

  public GetIpsExceptionFlag():boolean
  {
    return this._isIpsException;
  }
  public GetIpsInitStatusMessage():string
  {
    return this._IpsInitStatusMessage;
  }

  public onIpsWatch(position, watchActivity:string, autoWatch:boolean) 
  {
    console.log("IndoorAtlas.onIpsWatch IPS - " + watchActivity + " + :" + autoWatch, position);
    // console.log("IndoorAtlas.initialize.Success IPS GOOOOOOAAAAALLLLLLLL");
    // alert("IndoorAtlas.initialize.Success IPS GOOOOOOAAAAALLLLLLLL");
    if (!watchActivity) { watchActivity = Constants.WATCH_INITIALIZE; } // value not coming through?

    if (this.consoleLog)
    {
      if ((position) && (position.coords))
      {
        console.log("IndoorAtlas.initialize.onIpsWatch IPS: " + watchActivity + " + :" + autoWatch
          + " POSITION:"
          + position.coords.latitude + ", " + position.coords.longitude + ", " 
          + "floor: " + position.coords.floor);
      }
    }

    console.log("IndoorAtlas.onIpsWatch.Success IPS");
    this._isIpsInitialized = true;
    this.SetIpsStatus(true, false, "SETUP", "IPS15", "Indoor successfully initialized", false);
    this._IpsInitStatusMessage = "IPS19: " + "Successfully initialized";
    //sometime this one-time look up causes an error -- ignore for now
    //this.GetIpsLocationOneTime(true);  // get one position fix (if not watching)

    if (autoWatch)
    {
      if (this.consoleLog) console.log("GeolocationWrapper.onIpsWatch: success - leaving watch on");
      this.setWatchDependency(watchActivity, true, "Geolocation.OnIpsWatchSuccess");
    }
    else
    {
      if (this.consoleLog) console.log("GeolocationWrapper.onIpsWatch: success - but switching watch off");
      this.setWatchDependency(watchActivity, false, "Geolocation.OnIpsWatchSuccess");
    }
  }

  // // ------------------------------------------------------------

  // only call this when there is a valid, successful new position
  public SetPosition(position:any, positionType:string, watchType:string, geoStatus:GeoStatus):GeoPosition
  {
    //if (this.consoleLog) console.log("GeolocationWrapper.SetPosition " + positionType, position);
    let newPosition:GeoPosition = new GeoPosition();

    newPosition.GeoStatus = geoStatus;
    newPosition.PositionCount = geoStatus.PositionCount;

    newPosition.Success = true;  // this is ALWAYS true once the first position has been set
    
    // custom position attributes
    newPosition.PositionType = positionType;
    newPosition.MobileTimestamp = moment().format("YYYY-MM-DDTHH:mm:ss"); // "DT-L";
    newPosition.MobileTimestampUtc = moment().utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"; // "DT-Z";  

    // standard position attributes
    newPosition.coords    = position.coords;
    newPosition.Latitude  = position.coords.latitude;
    newPosition.Longitude = position.coords.longitude;
    newPosition.Altitude  = position.coords.altitude;
    newPosition.Accuracy  = position.coords.accuracy;
    newPosition.Heading   = position.coords.heading;

    newPosition.CoordsDisplay 
      = this.FormatCoordsForDisplay(position.coords.latitude, position.coords.longitude, 3);
    if (positionType === Constants.INDOOR)
    {
      newPosition.Floor     = position.coords.floor;
      newPosition.Timestamp = moment().unix() * 1000; // upper T - use epoch for Indoors to match GPS position
      newPosition.timestamp = moment().unix() * 1000; // lower t - use epoch for Indoors to match GPS position
    }
    else
    {
      newPosition.Floor     = null;
      newPosition.Timestamp = position.timestamp;    // upper T - GPS position -- assumed to be epoch
      newPosition.timestamp = position.timestamp;    // lower t - GPS position -- assumed to be epoch
    }
    newPosition.GpsTimestamp = this.GetPositionDateYMDHms(newPosition.timestamp);

    //if (this.consoleLog) console.log("GeolocationWrapper.SetPosition - GeoPosition:", newPosition);
    return newPosition;
  }

  private ProvideHintToIps(geoPosition:GeoPosition)
  {
    if (!this.IsIpsSupportedOnPlatform()) { return; }   // IPS requires NATIVE APP
    if (!this.IsOrganizationIpsEnabled()) { return; }   // must be enabled in Organization db

    // NOTE: we may lift this restriction later....once we can tell if the GPS is near a location indoors
    if (!this.isLocationIndoors()) { return; }          // must be in a known INDOOR location
    
    // ref: https://docs.indooratlas.com/develop/cordova/api/#indooratlas-setposition
    console.log("Geolocation.ProvideHintToIps", geoPosition);

    if (!this._isIpsInitialized) { return; }

    try
    {
      // call this whenever a new GPS Watch is started or for a GPS one-time location grab
      IndoorAtlas.setPosition( () => 
        {
          console.log("ProvideHintToIps - IndoorAtlas.setPosition.Success IPS");
          // apparently this callback is no longer necessary or supported
          // "setPosition (an useless success callback was also removed)"
        }
        , (error) => 
        {
          console.error("ProvideHintToIps - IndoorAtlas.setPosition.Error IPS", error);
        }
        , 
        { coordinates: [ geoPosition.Latitude, geoPosition.Longitude ]
        }
      );
    }
    catch(error)
    {
      console.error("Geolocation.ProvideHintToIps: EXCEPTION ", error);
    }
  }

  public FormatCoordsForDisplay(latitude, longitude, digits:number): string
  {
    if (isNaN(latitude) || isNaN(longitude)) { return ""; }
    return latitude.toFixed(digits) + "," + longitude.toFixed(digits);
  }

  public GetGoogleLatLng(latitude, longitude): any
  {
    // this is only used for mapping, so don't worry about an error in other cases
    try
    {
      return new google.maps.LatLng(parseFloat(latitude), parseFloat(longitude));
    }
    catch (error)
    {
      // this only seems to occur if the app is started with no internet connection and a location position is detected
      console.warn("GetGoogleLatLng ERROR", error);
      return null;
    }
  }

  public GetGooglePositionLatLng(geoPosition:GeoPosition): any
  {
    // this is only ued for mapping, so don't worry about an error in other cases
    try
    {
      return new google.maps.LatLng(parseFloat(geoPosition.Latitude), parseFloat(geoPosition.Longitude));
    }
    catch (error)
    {
      // this only seems to occur if the app is started with no internet connection and a location position is detected
      console.warn("GetGooglePositionLatLng ERROR", error);
      return null;
    }
  }

  // // ------------------------------------------------------------

  // use this to initialize each screen's location status
  public GetPosition():GeoPosition
  {
    //if (this.consoleLog) console.log("GeolocationWrapper.GetPosition BEST POSITION", this.bestPosition);
    return this.bestPosition;
  }
  public GetPositionLatitude():number
  {
    if (!this.bestPosition) { return 0; }
    return +this.bestPosition.Latitude; // NOTE:  "+" converts string to number;  converts null to zero
  }
  public GetPositionLongitude():number
  {
    if (!this.bestPosition) { return 0; }
    return +this.bestPosition.Longitude; // NOTE:  "+" converts string to number;  converts null to zero
  }

  public GetGpsPosition():GeoPosition
  {
    return this.gpsPosition;
  }

  public GetIpsPosition():GeoPosition
  {
    return this.ipsPosition;
  }

  // // ------------------------------------------------------------

  // use this to initialize each screen's location status
  public GetStatus():GeoStatus
  {
    // ToDo: later determine if and when we should return the last status (we may not care for IPS status some times)
    return this.gpsStatus;  // for now, always return the gps Status
    //return this.lastStatus;
  }

  public GetGpsStatus():GeoStatus
  {
    //console.log("GetGpsStatus", this.gpsStatus);
    return this.gpsStatus;
  }

  public GetIpsStatus():GeoStatus
  {
    return this.ipsStatus;
  }
  // // ------------------------------------------------------------

  /*
  SetGeolocationStatus(geoResult:GeoStatus): string
  {
    return geoResult.PositionType 
         + " #" + geoResult.PositionCount 
         + ": " + geoResult.ResultText 
         + (geoResult.Success) ? "" : " (" + geoResult.ResultCode + ")"
         ;
  }
  */

  CreateEmptyPosition(positionType:string, geoResult:GeoStatus): GeoPosition
  {
    // this is only intended for the situation where a position has never been found
    // and we need to return an error status within the position object
    let emptyPosition:GeoPosition = new GeoPosition();
    emptyPosition.GeoStatus = geoResult;
    emptyPosition.Success = false;                // e.g. true 
    emptyPosition.PositionCount = 0;
    emptyPosition.PositionType = positionType;    // e.g. "INDOOR" | "GPS"

    emptyPosition.MobileTimestamp = moment().format("YYYY-MM-DDTHH:mm:ss"); // "DT-L";
    emptyPosition.MobileTimestampUtc = moment().utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"; // "DT-Z";  

    // standard position attributes
    emptyPosition.coords = null;
    emptyPosition.Latitude = "0";
    emptyPosition.Longitude = "0";

    emptyPosition.CoordsDisplay = "pending";
    
    emptyPosition.Altitude = null;
    emptyPosition.Accuracy = null;
    emptyPosition.Heading = null;
    emptyPosition.Timestamp = moment().unix() * 1000; // assumed to be epoch
    emptyPosition.timestamp = moment().unix() * 1000; // assumed to be epoch
    emptyPosition.GpsTimestamp = this.GetPositionDateYMDHms(emptyPosition.timestamp);

    // indoor only position attributes
    emptyPosition.Floor = null;                   // e.g. position.coords.floor;
    return emptyPosition;
  }

  public GetGpsLocationOneTime(isCalledFromInitialize:boolean)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.GetGpsLocationOneTime: setup");

    //if (!isCalledFromInitialize) { this.InitializeGps(); }

    if (this.isGpsWatching()) 
    {
      if (this.consoleLog) console.log("GeolocationWrapper.GetGpsLocationOneTime: already watching");
      if (this.gpsObserver) this.gpsObserver.next(this.gpsPosition);  // return the GPS position anyway
      return;
    }

    if (this.consoleLog) console.log("GeolocationWrapper.GetGpsLocationOneTime: READY");

    //this.geolocationPlugin.getCurrentPosition(this.onGpsPositionOneTimeSuccess, this.onGpsPositionOneTimeError); // old code -- changed in Ionic v4
    this.geolocationPlugin.getCurrentPosition().then((position) => 
    {
      if (this.consoleLog) console.log("GeolocationWrapper.GetGpsLocationOneTime: OK", position);
      this.onGpsPositionOneTimeSuccess(position);

      if (isCalledFromInitialize)
      {
        if (this.consoleLog) console.log("GeolocationWrapper.GetGpsLocationOneTime: call InitializeWatchDefault for one-time start");
        this.InitializeWatchDefault(true);
      }
    })
    .catch((error) => 
    {
      console.warn('GeolocationWrapper.GetGpsLocationOneTime: ERROR', error);
      this.onGpsPositionOneTimeError(error);
    });
  }

  public onGpsPositionOneTimeSuccess(position:any)
  {
    //if (this.consoleLog) console.log("GeolocationWrapper.watchPosition RESPONSE", position); // may or may not be okay
    let notifyPosition = this.onGpsResponse(position, Constants.GEO_CURRENT, Constants.GEO_SUCCESS_CURRENT);
    if (this.gpsObserver) this.gpsObserver.next(notifyPosition); 
    this.ProvideHintToIps(notifyPosition);
  }

  public onGpsPositionOneTimeError(error:any)
  {
    //if (this.consoleLog) 
    console.warn("GeolocationWrapper.GetGpsLocationOneTime GPS ERROR", error);
    this.onGpsError(error, Constants.GEO_CURRENT);
  }

  // --------------------------------------------------------------------------

  public onIpsCurrentPositionSuccess(position:any)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.onIpsCurrentPositionSuccess RESPONSE", position); // may or may not be okay
    let notifyPosition = this.onIpsResponse(position, Constants.GEO_CURRENT, Constants.GEO_SUCCESS_CURRENT);
    if (this.ipsObserver) this.ipsObserver.next(notifyPosition); 
  }
  public onIpsCurrentPositionError(error:any)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.onIpsCurrentPositionError IPS ERROR", error);
    let notifyPosition = this.onIpsError(error, Constants.GEO_CURRENT);
    if (this.ipsObserver) this.ipsObserver.next(notifyPosition); 
  }

  public GetIpsLocationOneTime(isCalledFromInitialize:boolean)
  {
    if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime: setup");

    if (!this.IsIpsAvailableForOrgAndPlatform())
    {
      if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime: cannot proceed");
      return;
    }

    if (!this._isIpsInitialized)
    {
      if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime: not initialized -- returning here");
      //if (!isCalledFromInitialize) { this.InitializeIps(); }
      return;
    }

    if (this.isIpsWatching()) 
    {
      if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime: already watching");
      if (this.ipsObserver) this.ipsObserver.next(this.ipsPosition);  // return the GPS position anyway
      return;
    }

    if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime: READY");
    IndoorAtlas.getCurrentPosition()
    .then
      ( this.onIpsCurrentPositionSuccess
      , this.onIpsCurrentPositionError );
    /*
    .then(position => 
      {
        if (this.consoleLog) console.log("GeolocationWrapper.watchPosition RESPONSE", position); // may or may not be okay
        let notifyPosition = this.onIpsResponse(position, Constants.GEO_CURRENT);
        if (this.ipsObserver) this.ipsObserver.next(notifyPosition); 
      }
      , error => 
      {
        if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime IPS ERROR", error);
        let notifyPosition = this.onGpsError(error, Constants.GEO_CURRENT);
        if (this.consoleLog) console.log("GeolocationWrapper.GetIpsLocationOneTime IPS ERROR #" + notifyPosition.PositionCount + " "
                                                                            + notifyPosition.ResultText);
        if (this.ipsObserver) this.ipsObserver.next(notifyPosition); 
      }
    );
    */
  }

  isIpsWatching() 
  {
    return (this._IpsWatchId === null)? false : true;
  }

  isGpsWatching() 
  {
    return (this._GpsWatchId === null)? false : true;
  }

  // ------------------------------------------------------------------------
  // Monitoring & Map screens require that the location watch in running
  // ------------------------------------------------------------------------
  setWatchDependency(watchActivity:string, isWatchDependent:boolean, context:string)
  {
    if (!watchActivity) { watchActivity = Constants.WATCH_INITIALIZE; }  // This should never happen btw...

    if (this.consoleLog) console.log("geolocation.setWatchDependency: " + watchActivity + ":" + isWatchDependent + " - " + context);

    if (this.activeWatches.indexOf(watchActivity) === -1)
    {
      // we know now that it wasn't dependent before, but it is now so add it
      if (isWatchDependent)
      {
        this.activeWatches.push(watchActivity);
      }
    }
    else
    {
      // we know now that it was dependent before, but it no longer is dependent so remove it
      if (!isWatchDependent)
      {
        let index = this.activeWatches.indexOf(watchActivity);
        if(index !== -1)
        {
          this.activeWatches.splice(index, 1);
        }
      }
    }

    // HACK ALERT: Trying to keep the active watches "clean"
    // if all that is left is WATCH_INITIALIZE, then let's remove it and assume that the IPS initialization is complete
    if ((this.activeWatches) && (this.activeWatches.length > 0))
    {
      let index = this.activeWatches.indexOf(Constants.WATCH_INITIALIZE);
      if(index !== -1)
      {
        this.activeWatches.splice(index, 1);  // remove the initialization from active watches
      }
    }

    // get the final counts
    if ((this.activeWatches) && (this.activeWatches.length > 0))
    {
      this._isActiveWatchDependency = true;
    }
    else
    {
      this._isActiveWatchDependency = false;
    }
    
    // if anything is depedent on location watch, then make sure that is running
    if (this.consoleLog) console.log("geolocation.setWatchDependency: " + watchActivity + ":" + isWatchDependent + " isActiveWatchDependency:" + this._isActiveWatchDependency + " - " + context, this.activeWatches);
    if ( this._isActiveWatchDependency )
    {
      if (!this.isGpsWatching())
      {
        this.GpsWatch(true);
      }
  
      // are we near a known INDOOR location?  If so, then consider starting IPS watch as well
      if (!this.isIpsWatching())
      {
        this.IpsWatch(true); // only do this if we are in an indoor site
      }
    }
    else
    {
      // otherwise end the location watch as nobody cares about it
      this.GpsWatch(false);
      this.IpsWatch(false);
    }
  }

  isWatchDependent(watchActivity:string):boolean
  {
    if (this.consoleLog) console.log("geolocation.isWatchDependent: " + watchActivity);

    if (this.activeWatches.indexOf(watchActivity) === -1)
    {
      if (this.consoleLog) console.log("geolocation.isWatchDependent: " + watchActivity + ":false", this.activeWatches);
      return false;
    }

    if (this.consoleLog) console.log("geolocation.isWatchDependent: " + watchActivity + ":true", this.activeWatches);
    return true;

    /*
    if (watchActivity === Constants.WATCH_MONITOR)
    {
      console.log("geolocation.isWatchDependent: " + watchActivity + ":" + this._isActiveMonitor);
      return this._isActiveMonitor;
    }
    if (watchActivity === Constants.WATCH_MAP)
    {
      console.log("geolocation.isWatchDependent: " + watchActivity + ":" + this._isActiveMap);
      return this._isActiveMap;
    }
    if (watchActivity === Constants.WATCH_SETTINGS)
    {
      console.log("geolocation.isWatchDependent: " + watchActivity + ":" + this._isActiveSettings);
      return this._isActiveSettings;
    }
    return false;
    */
  }

  getWatchDependencyList():string
  {
    if (this.consoleLog) console.log("geolocation.getWatchDependencyList: " + this._isActiveWatchDependency, this.activeWatches);

    if (!this._isActiveWatchDependency)
    {
      return this.myTranslate("There are no location-watch dependencies");
    }

    let separator = "";
    let result = ""; // this.myTranslate("The following are dependent on location watching") + ": ";
    
    let counter:number = 0;
    this.activeWatches.forEach(activeWatch => 
    {
      counter++;
      if (this.consoleLog) console.log("geolocation.getWatchDependencyList: counter " + activeWatch, activeWatch);
      result = result + separator + counter + ":" + this.myTranslate(activeWatch);
      separator = ", ";
    });

    return result;

    /*
    if ( (!this._isActiveMonitor) 
      && (!this._isActiveMap) 
      && (!this._isActiveSettings) 
       )
    {
      return "There are no location watch dependencies";
    }

    let separator = "";
    let result = "The following are dependent on location watching" 
               + ": ";
    
    if (this._isActiveMonitor)
    {
      result = result + separator + "Monitor"
      separator = ", ";
    }
    if (this._isActiveMap)
    {
      result = result + separator + "Map"
      separator = ", ";
    }
    if (this._isActiveSettings)
    {
      result = result + separator + "Settings"
      separator = ", ";
    }

    return result;
    */
  }

  isWatchDependency()
  {
    return this._isActiveWatchDependency; //( (this._isActiveMonitor) || (this._isActiveMap) );
  }

  // ------------------------------------------------------------------------
  // Indoor Processing logic
  // ------------------------------------------------------------------------

  _EnableIpsProcessing()
  {
    if (this.consoleLog) console.log("GeolocationService._EnableIpsProcessing"); 

    let isActiveMonitor:boolean = this.isWatchDependent(Constants.WATCH_MONITOR);
    if ( (isActiveMonitor) && (!this.isIpsWatching()) ) // active monitor but not watching yet
    {
      if (this._isIpsInitialized)
      {
        if (this.consoleLog) console.log("GeolocationService._EnableIpsProcessing: active monitor but need to start IPS watch"); 
        //this.IpsWatch(true);
        this.setWatchDependency(Constants.WATCH_MONITOR, true, "Geolocation._EnableIpsProcessing");
      }
      else
      {
        if (this.consoleLog) console.log("GeolocationService._EnableIpsProcessing: active monitor but need to initialize IPS first and then start watch"); 
        this.InitializeIps(Constants.WATCH_MONITOR, true);
      }
    }
    else
    {
      if (this.consoleLog) console.log("GeolocationService._EnableIpsProcessing: Need to initialize IPS"); 
      this.InitializeIps(Constants.WATCH_INITIALIZE, false);
    }
  }

  _DisableIpsProcessing()
  {
    if (this.consoleLog) console.log("GeolocationService._DisableIpsProcessing"); 
    if (this.isIpsWatching())
    {
       this.IpsWatch(false);
    }
  }

  //isNearIndoorCheckinSite()
  isLocationIndoors()
  {
    return this.globalProvider.AmbitConfig.CheckinSiteIndoorFlag;
    //return this._checkinSiteIndoorFlag;
    //_checkinSiteIndoorFlag = $localStorage.CheckinSiteIndoorFlag || false;
    //return _checkinSiteIndoorFlag;
  }

  // called from the Checkin screen to start or enable the IPS watch (if applicable)
  //public ProcessCheckinSiteIndoor(selectedCheckinSite)
  public SetLocationIndoors(isIndoorFlag:boolean)
  {
    if (this.consoleLog) console.log("GeolocationService.SetLocationIndoors: " + isIndoorFlag);

    this.globalProvider.SetStorageObject_AmbitConfig_CheckinSiteIndoorFlag(isIndoorFlag);
    //this._checkinSiteIndoorFlag = isIndoorFlag;
    
    if (isIndoorFlag)
    {
      this._EnableIpsProcessing();
    }
    else
    {
      this._DisableIpsProcessing();
    }


    //if (this.consoleLog) console.log("GeolocationService.ProcessCheckinSiteIndoor", selectedCheckinSite); 
    //$localStorage.CheckinSite = selectedCheckinSite;

    /*
    if (selectedCheckinSite)
    {
      //$localStorage.CheckinSiteId = selectedCheckinSite.SiteId;
      if (selectedCheckinSite.IndoorFlag === true)
      {
        this._checkinSiteIndoorFlag = true;
        //$localStorage.CheckinSiteIndoorFlag = true;
        this._EnableIndoorProcessing();
      }
      else
      {
        this._checkinSiteIndoorFlag = false;
        //$localStorage.CheckinSiteIndoorFlag = false;
        this._DisableIndoorProcessing();
      }
    }
    else
    {
      this._checkinSiteIndoorFlag = false;
    }
    */
  }

  // ------------------------

  _saveBestLocationForApiCalls(lastPosition:GeoPosition, positionType)
  {  
    if (this.consoleLog) console.log("GeolocationService._saveBestLocationForApiCalls - " + positionType);

    // decide which location to store for the next API call
    // if this latest position is GPS but we have INDOOR WATCH running, then ignore this GPS
    if (positionType === Constants.GPS)
    {
      if (   (this.isIpsWatching() === true)
          && (this.IsPositionGoodEnough(this.ipsPosition)) 
         )
      {
        if (this.consoleLog) console.log("GeolocationService._saveBestLocationForApiCalls - " + positionType
          + " - IGNORE as we have a better INDOOR location already");
        return this.ipsPosition;
      }
    }

    // store this result:  ToDo- MOVE CODE INTO THIS SERVICE INSTEAD OF COMMON
    //this.SaveLocationInLocalStorage
    //     (lastPosition, lastPosition.PositionCount, lastPosition.ResultCode, lastPosition.ResultText, lastPosition.PositionType); 

    return lastPosition;
  }

  // ---------------------------------------------------------------------------

  IsPositionGoodEnough(geoPosition:GeoPosition):boolean
  {
    if (this.consoleLog) console.log("GeolocationService.IsPositionGoodEnough");
    if ( this.IsPositionFresh(geoPosition, this._defaultFreshnessThresholdMinutes) 
      && this.IsPositionAccurate(geoPosition) ) 
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionGoodEnough: YES");
      return true;
    }
    else
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionGoodEnough: NO");
      return false;
    }
  }

  public RefreshStalePosition()
  {
    if (this.isGpsWatching())
    {
      if (this.consoleLog) console.log("GeolocationService.RefreshStalePosition: not required as already watching");
      return;
    }

    if (this.IsPositionGoodEnough(this.lastPosition))
    {
      if (this.consoleLog) console.log("GeolocationService.RefreshStalePosition: not required as last position was good enough", this.lastPosition);
      return;
    }

    if (this.consoleLog) console.log("GeolocationService.RefreshStalePosition: trigger the one-time location to keep things fresh");
    this.GetGpsLocationOneTime(false);

    // ToDo: maybe set a WATCH until first location is grabbed, then switch off?
  }

  IsPositionAccurate(geoPosition:GeoPosition)
  {
    //if (consoleLog) if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate");
    if (!geoPosition)
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - NO POSITION");
      return false;
    }

    if (!geoPosition.coords)
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - NO COORDINATES FOUND");
      return false;
    }

    if (!geoPosition.Accuracy) // coords.accuracy
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - NO ACCURACY FOUND");
      return false;
    }

    //if (consoleLog) if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - AccuracyMeters: " + position.coords.accuracy);

    let accuracyMeters = this.common.GetNumberFromString(geoPosition.Accuracy, 0, "GeoPosition.Accuracy"); // position.coords.accuracy;
    if (accuracyMeters > this._defaultAccuracyThresholdMeters)
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - NO: accuracy(" + accuracyMeters + ") > threshold(" + this._defaultAccuracyThresholdMeters + ")");
      return false;
    }

    if (this.consoleLog) console.log("GeolocationService.IsPositionAccurate - YES: accuracy(" + accuracyMeters + ") <= threshold(" + this._defaultAccuracyThresholdMeters + ")");
    return true;
  }

  IsPositionFresh(geoPosition:GeoPosition, FreshnessThresholdMinutes:number):boolean
  {
    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh");
    if (!geoPosition)
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - NO POSITION");
      return false;
    }

    if (!geoPosition.timestamp)
    {
      if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - NO TIMESTAMP");
      return false;
    }

    //console.log("GeolocationService.IsPositionFresh - 1a) geoPosition.timestamp:" + geoPosition.timestamp);
    //let positionDate1 = this.GetPositionDateNormalized(geoPosition.timestamp);
    //console.log("GeolocationService.IsPositionFresh - 1b) positionDate1:" + positionDate1);
    //let ageMinutes1 = moment().diff(positionDate1, 'minutes');
    //console.log("GeolocationService.IsPositionFresh - 1c) ageMinutes1:" + ageMinutes1);

    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - geoPosition.timestamp:" + geoPosition.timestamp);
    let positionDate = moment(geoPosition.timestamp);
    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - positionDate:" + positionDate);
    let ageMinutes = moment().diff(positionDate, 'minutes');
    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - ageMinutes:" + ageMinutes);
    
    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - "
                + " Timestamp: " + moment(positionDate).format("YYYY-MM-DDTHH:mm:ss")
                + " AgeMinutes: " + ageMinutes
                + " Humanized: " + moment.duration(moment().diff(positionDate)).humanize()
               );

    if (ageMinutes > FreshnessThresholdMinutes)
    {
      if (this.consoleLog) console.warn("GeolocationService.IsPositionFresh - NO: age(" + ageMinutes + ") > threshold(" + FreshnessThresholdMinutes + ") minutes");
      return false;
    }

    if (this.consoleLog) console.log("GeolocationService.IsPositionFresh - YES: age(" + ageMinutes + ") <= threshold(" + FreshnessThresholdMinutes + ") minutes");
    return true;
  }

  GetPositionDateNormalized(inPositionTimestamp:number): Date
  { 
      let positionDate = new Date();  // position.timestamp
      let ageYears = moment().diff(positionDate, 'years');
      if (ageYears > 0)
      {
        // fix a weird bug in Safari on my Mac OSX which has the correct time, but in 1984!
        let positionTimestampString = moment(inPositionTimestamp).format("YYYY-MM-DDTHH:mm:ss");
        //if (this.consoleLog) console.log("positionTimestampString: " + positionTimestampString);
        let positionTime = positionTimestampString.substring(11,19);
        //if (this.consoleLog) console.log("positionTime: " + positionTime);
        let assumeTodaysDate = moment().format('YYYY-MM-DD') + 'T' + positionTime;
        //if (this.consoleLog) console.log("assumeTodaysDate: " + assumeTodaysDate);
        positionDate = moment(assumeTodaysDate).toDate(); 
      }
      return positionDate;
  }

  GetPositionDateYMDHms(inPositionTimestamp:number): string
  { 
      let positionDate = new Date();  // position.timestamp
      let positionTimestampString = "";
      let ageYears = moment().diff(positionDate, 'years');
      if (ageYears > 0) // this shouldn't be greater than zero but weird Safari bug 
      {
        // fix a weird bug in Safari on my Mac OSX which has the correct time, but in 1984!
        positionTimestampString = moment(inPositionTimestamp).format("YYYY-MM-DDTHH:mm:ss");
        //if (this.consoleLog) console.log("positionTimestampString: " + positionTimestampString);
        let positionTime = positionTimestampString.substring(11,19);
        //if (this.consoleLog) console.log("positionTime: " + positionTime);
        let assumeTodaysDate = moment().format('YYYY-MM-DD') + 'T' + positionTime;
        //if (this.consoleLog) console.log("assumeTodaysDate: " + assumeTodaysDate);
        positionDate = moment(assumeTodaysDate).toDate(); 
        positionTimestampString = moment(positionDate).format("YYYY-MM-DDTHH:mm:ss");
      }
      else
      {
        positionTimestampString = moment(inPositionTimestamp).format("YYYY-MM-DDTHH:mm:ss");
      }
      return positionTimestampString;
  }

  // --------------------------------------------------------
  // OPTIONS:  immediate with no callback
  // --------------------------------------------------------
  getIpsOptions()
  {
      return this._IpsOptions;
  }
  getGpsOptions()
  {
      return this._GpsOptions;
  }
}
